import type { ComponentChildren, JSX, RefObject } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Portal } from './Portal';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useHotkey } from '@/hooks/useHotkey';
import { cn } from '@/lib/cn';

export type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'right';

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchor: RefObject<HTMLElement>;
  placement?: Placement;
  offset?: number;
  children: ComponentChildren;
  className?: string;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  /** Match the popover's min-width to the anchor's width. Useful for Select-like menus. */
  matchAnchorWidth?: boolean;
  /** Role applied to the popover element. Restringido al union de roles
   * ARIA válidos para que el typo no compile (audit P2 #56). */
  role?: JSX.AriaRole;
  'aria-label'?: string;
}

interface Position {
  top: number;
  left: number;
}

function computePosition(
  anchor: HTMLElement,
  popover: HTMLElement,
  placement: Placement,
  offset: number,
): Position {
  const a = anchor.getBoundingClientRect();
  // Tamaño de layout, no `getBoundingClientRect()`: al abrir, `animate-scale-in`
  // lo encoge con un transform y el recorte contra el borde se calculaba con un
  // ancho menor que el real (el selector de fechas de la cabecera salía cortado
  // por la derecha). El ResizeObserver no avisa de transforms, así que no se corregía.
  const p = { width: popover.offsetWidth, height: popover.offsetHeight };
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = 0;
  let left = 0;

  switch (placement) {
    case 'top':
      top = a.top - p.height - offset;
      left = a.left + (a.width - p.width) / 2;
      break;
    case 'top-start':
      top = a.top - p.height - offset;
      left = a.left;
      break;
    case 'top-end':
      top = a.top - p.height - offset;
      left = a.right - p.width;
      break;
    case 'bottom':
      top = a.bottom + offset;
      left = a.left + (a.width - p.width) / 2;
      break;
    case 'bottom-start':
      top = a.bottom + offset;
      left = a.left;
      break;
    case 'bottom-end':
      top = a.bottom + offset;
      left = a.right - p.width;
      break;
    case 'left':
      top = a.top + (a.height - p.height) / 2;
      left = a.left - p.width - offset;
      break;
    case 'right':
      top = a.top + (a.height - p.height) / 2;
      left = a.right + offset;
      break;
  }

  // Viewport-edge clamp.
  const margin = 8;
  left = Math.max(margin, Math.min(left, vw - p.width - margin));
  top = Math.max(margin, Math.min(top, vh - p.height - margin));

  return { top: top + window.scrollY, left: left + window.scrollX };
}

export function Popover({
  open,
  onClose,
  anchor,
  placement = 'bottom-start',
  offset = 6,
  children,
  className,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  matchAnchorWidth = false,
  role = 'dialog',
  'aria-label': ariaLabel,
}: PopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Position | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useClickOutside(
    popoverRef,
    (event) => {
      if (!closeOnOutsideClick) return;
      const target = event.target as Node | null;
      if (anchor.current && target && anchor.current.contains(target)) return;
      onClose();
    },
    open,
  );

  useHotkey('Escape', onClose, { enabled: open && closeOnEscape, allowInEditable: true });

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const a = anchor.current;
      const p = popoverRef.current;
      if (!a || !p) return;
      if (matchAnchorWidth) setWidth(a.getBoundingClientRect().width);
      setPos(computePosition(a, p, placement, offset));
    };
    update();
    const ro = new ResizeObserver(update);
    if (popoverRef.current) ro.observe(popoverRef.current);
    if (anchor.current) ro.observe(anchor.current);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, placement, offset, anchor, matchAnchorWidth]);

  if (!open) return null;

  return (
    <Portal layer="popover">
      <div
        ref={popoverRef}
        role={role}
        aria-label={ariaLabel}
        className={cn(
          'border-border bg-surface animate-scale-in fixed rounded-md border shadow-lg outline-none',
          className,
        )}
        style={{
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          minWidth: width,
          // Hide visually until positioned to avoid flash.
          visibility: pos ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </Portal>
  );
}

export function useFloatingState(initial = false): {
  open: boolean;
  setOpen: (next: boolean) => void;
  toggle: () => void;
  close: () => void;
} {
  const [open, setOpen] = useState(initial);
  return {
    open,
    setOpen,
    toggle: () => setOpen((prev) => !prev),
    close: () => setOpen(false),
  };
}
